import React, { useEffect, useState } from 'react';
import styles from '../styles/CreatorPromotions.module.scss';
import { GoTag } from "react-icons/go";
import { MdOutlinePeople } from 'react-icons/md';
import { HiOutlinePlusSmall } from "react-icons/hi2";
import api from '../../../utils/api';
import Spinner from '../../../components/Spinner';
import { CreatePromoSchema } from '../schemas/promoSchema';


type Status = 'active' | 'paused' | 'expired';
type DiscountType = 'percentage' | 'flat';

interface Usage {
  id: string;
  name: string;
  initials: string;
  date: string;
  discount: number;
}

interface Promo {
  id: string;
  code: string;
  discount: number;
  type: DiscountType;
  status: Status;
  used: number;
  limit: number | null;
  perUserLimit: number | null;
  expires: string | null;
  revenue: number;
  usages: Usage[];
  minOrderAmount: number
}


const Ico = {
  copy: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  ),
  search: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  dollar: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  trend: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  activity: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  edit: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  trash: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6M9 6V4h6v2" />
    </svg>
  ),
  pause: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
    </svg>
  ),
  play: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  chevron: (open: boolean) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s' }}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  close: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  check: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
};

const genCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const pct = (used: number, limit: number | null) =>
  limit ? Math.min(Math.round((used / limit) * 100), 100) : 0;

function PromoUsagePanel({ promo }: { promo: Promo }) {
  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        {<MdOutlinePeople />}&nbsp; Recent Redemptions — {promo.used} total
      </div>

      {promo.usages.length === 0 ? (
        <p className={styles.panelEmpty}>No redemptions yet.</p>
      ) : (
        <>
          <div className={styles.usageList}>
            {promo.usages.slice(0, 5).map(u => (
              <div key={u.id} className={styles.usageRow}>
                <div className={styles.ava}>{u.initials}</div>
                <div className={styles.uInfo}>
                  <div className={styles.uName}>{u.name}</div>
                  <div className={styles.uDate}>{u.date}</div>
                </div>
                <div className={styles.uDisc}>
                  {promo.type === 'percentage' ? `−${u.discount}%` : `−$${u.discount}`}
                </div>
              </div>
            ))}
          </div>
          {promo.used > 5 && (
            <div className={styles.moreNote}>
              +{promo.used - 5} more redemptions not shown
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface PromoActionsProps {
  promo: Promo;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

function PromoActions({ promo, expanded, onToggleExpand, onToggleStatus, onDelete }: PromoActionsProps) {
  return (
    <div className={styles.acts}>
      <button
        className={`${styles.iBtn} ${expanded ? styles.open : ''}`}
        title="View uses"
        onClick={onToggleExpand}
      >
        {<MdOutlinePeople />}
      </button>
      {promo.status !== 'expired' && (
        <button
          className={styles.iBtn}
          title={promo.status === 'active' ? 'Pause' : 'Activate'}
          onClick={onToggleStatus}
        >
          {promo.status === 'active' ? Ico.pause : Ico.play}
        </button>
      )}
      <button className={styles.iBtn} title="Edit">{Ico.edit}</button>
      <button
        className={`${styles.iBtn} ${styles.del}`}
        title="Delete"
        onClick={onDelete}
      >
        {Ico.trash}
      </button>
      <button
        className={`${styles.iBtn} ${expanded ? styles.open : ''}`}
        onClick={onToggleExpand}
      >
        {Ico.chevron(expanded)}
      </button>
    </div>
  );
}

const CreatorPromotions: React.FC = () => {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [filter, setFilter] = useState<'all' | Status>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);


  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState('10');
  const [discType, setDiscType] = useState<DiscountType>('percentage');
  const [usageLimit, setUsageLimit] = useState('');
  const [expires, setExpires] = useState('');
  const [minOrderAmount, setMinOrderAmount] = useState<number>()
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pop = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  };

  const visible = promos.filter(p => {
    const matchF = filter === 'all' || p.status === filter;
    const matchS = p.code.toLowerCase().includes(search.toLowerCase());
    return matchF && matchS;
  });

  const totalUsed = promos.reduce((a, p) => a + p.used, 0);
  const totalRevenue = promos.reduce((a, p) => a + p.revenue, 0);
  const activeCount = promos.filter(p => p.status === 'active').length;

  const handleCopy = (c: string) => {
    navigator.clipboard.writeText(c).catch(() => { });
    pop(`"${c}" copied`);
  };

  const handleDelete = (id: string) => {
    setPromos(prev => prev.filter(p => p.id !== id));
    pop('Promo code deleted');
  };

  const handleToggle = (id: string) => {
    setPromos(prev => prev.map(p => {
      if (p.id !== id) return p;
      return { ...p, status: p.status === 'active' ? 'paused' : 'active' };
    }));
  };

  const handleCreate = async () => {
    if (!code.trim()) return;
    const newPromo: Promo = {
      id: Date.now().toString(),
      code: code.trim().toUpperCase(),
      discount: Number(discount) || 10,
      type: discType,
      status: 'active',
      used: 0,
      limit: usageLimit ? Number(usageLimit) : null,
      perUserLimit: 1, //Default is one for per user
      expires: expires || null,
      revenue: 0,
      usages: [],
      minOrderAmount: minOrderAmount || 0
    };

    const formattedPromo = {
      code: newPromo.code,
      discountType: newPromo.type,
      discountValue: newPromo.discount,
      maxDiscount: 500, //Default is 500 Todo add maxDiscount in the popup
      minOrderAmount: Number(newPromo.minOrderAmount) || 0,
      usageLimit: newPromo.limit || null,
      perUserLimit: newPromo.perUserLimit || 1,
      expiresAt: newPromo.expires
        ? new Date(newPromo.expires).toISOString()
        : null,
    };

    const result = CreatePromoSchema.safeParse(formattedPromo);
    if (!result.success) {
      setError("Please fill all required fields correctly");
      return;
    }
    const formattedPromoResult = result.data;
    await api.post('/promos', formattedPromoResult)
    setPromos(prev => [newPromo, ...prev]);
    setShowModal(false);
    setError(null);
    setCode(''); setDiscount('10'); setDiscType('percentage'); setUsageLimit(''); setExpires('');
    pop(`"${newPromo.code}" created!`);
  };

  const fillClass = (p: Promo) => {
    const v = pct(p.used, p.limit);
    if (v >= 100) return styles.over;
    if (v >= 80) return styles.warn;
    return '';
  };


  useEffect(() => {
    const fetchPromoData = async () => {
      try {
        const response = await api.get('/creators/promos/analytics');
        setPromos(response.data);
      } catch (error) {
        console.error('Error fetching promo data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchPromoData();
  }, []);

  if (loading) return <Spinner />
  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerMeta}>
          <h1>Promo Codes</h1>
          <p>Create and track discount codes for your audience</p>
        </div>
        <button className={styles.newBtn} onClick={() => setShowModal(true)}>
          {<HiOutlinePlusSmall size={20} />} New Promo Code
        </button>
      </div>

      {/* ── Stats ── */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.sIcon}>{<GoTag />}</div>
          <div className={styles.sVal}>{promos.length}</div>
          <div className={styles.sLabel}>Total Codes</div>
          <div className={styles.sBadge}>{activeCount} active</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.sIcon}>{<MdOutlinePeople />}</div>
          <div className={styles.sVal}>{totalUsed.toLocaleString()}</div>
          <div className={styles.sLabel}>Total Uses</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.sIcon}>{Ico.dollar}</div>
          <div className={styles.sVal}>${totalRevenue.toLocaleString()}</div>
          <div className={styles.sLabel}>Revenue Generated</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.sIcon}>{Ico.activity}</div>
          <div className={styles.sVal}>{activeCount}</div>
          <div className={styles.sLabel}>Active Codes</div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <div className={styles.tabs}>
          {(['all', 'active', 'paused', 'expired'] as const).map(f => (
            <button
              key={f}
              className={`${styles.tab} ${filter === f ? styles.active : ''}`}
              onClick={() => setFilter(f)}
            >
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className={styles.search}>
          {Ico.search}
          <input
            placeholder="Search codes…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className={styles.tableCard}>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Status</th>
                <th>Usage</th>
                <th>Revenue</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className={styles.empty}>
                      <div className={styles.emptyIco}>🏷️</div>
                      <h3>No codes found</h3>
                      <p>Try a different filter or create a new promo.</p>
                    </div>
                  </td>
                </tr>
              )}

              {visible.map(p => (
                <React.Fragment key={p.id}>
                  <tr className={`${styles.row} ${expanded === p.id ? styles.expanded : ''}`}>
                    <td className={styles.td}>
                      <span className={styles.chip}>
                        {p.code}
                        <button onClick={() => handleCopy(p.code)} title="Copy">{Ico.copy}</button>
                      </span>
                    </td>

                    <td className={styles.td}>
                      <span className={styles.disc}>
                        {p.type === 'percentage' ? `${p.discount}%` : `$${p.discount}`}
                        <small>{p.type === 'percentage' ? 'off' : 'fixed'}</small>
                      </span>
                    </td>

                    <td className={styles.td}>
                      <span className={`${styles.badge} ${styles[p.status]}`}>
                        {p?.status ? p.status[0].toUpperCase() + p.status.slice(1) : ''}
                      </span>
                    </td>

                    <td className={styles.td}>
                      <div className={styles.usageCell}>
                        <div className={styles.usageTop}>
                          <strong>{p?.used?.toLocaleString()}</strong>
                          <span>/ {p?.limit?.toLocaleString() ?? '∞'}</span>
                        </div>
                        {p.limit && (
                          <div className={styles.bar}>
                            <span className={fillClass(p)} style={{ width: `${pct(p.used, p.limit)}%` }} />
                          </div>
                        )}
                      </div>
                    </td>

                    <td className={styles.td}>
                      <div className={styles.rev}>
                        ${p?.revenue.toLocaleString()}
                        <small>generated</small>
                      </div>
                    </td>

                    <td className={styles.td}>
                      <span className={`${styles.exp} ${p.status === 'expired' ? styles.past : ''}`}>
                        {p.expires ?? '—'}
                      </span>
                    </td>

                    <td className={styles.td}>
                      <PromoActions
                        promo={p}
                        expanded={expanded === p.id}
                        onToggleExpand={() => setExpanded(prev => prev === p.id ? null : p.id)}
                        onToggleStatus={() => handleToggle(p.id)}
                        onDelete={() => handleDelete(p.id)}
                      />
                    </td>
                  </tr>

                  {expanded === p.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <PromoUsagePanel promo={p} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {visible.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIco}>🏷️</div>
              <h3>No codes found</h3>
              <p>Try a different filter or create a new promo.</p>
            </div>
          ) : (
            visible.map(p => (
              <article
                key={`${p.id}-mobile`}
                className={`${styles.mobileCard} ${expanded === p.id ? styles.mobileCardExpanded : ''}`}
              >
                <div className={styles.mobileCardTop}>
                  <span className={styles.chip}>
                    {p.code}
                    <button onClick={() => handleCopy(p.code)} title="Copy">{Ico.copy}</button>
                  </span>
                  <span className={`${styles.badge} ${styles[p.status]}`}>
                    {p?.status ? p.status[0].toUpperCase() + p.status.slice(1) : ''}
                  </span>
                </div>

                <div className={styles.mobileMetaRow}>
                  <span className={styles.mobileMetaLabel}>Discount</span>
                  <span className={styles.disc}>
                    {p.type === 'percentage' ? `${p.discount}%` : `$${p.discount}`}
                    <small>{p.type === 'percentage' ? ' off' : ' fixed'}</small>
                  </span>
                </div>

                <div className={styles.mobileMetaRow}>
                  <span className={styles.mobileMetaLabel}>Usage</span>
                  <div className={styles.usageCell}>
                    <div className={styles.usageTop}>
                      <strong>{p?.used?.toLocaleString()}</strong>
                      <span>/ {p?.limit?.toLocaleString() ?? '∞'}</span>
                    </div>
                    {p.limit && (
                      <div className={styles.bar}>
                        <span className={fillClass(p)} style={{ width: `${pct(p.used, p.limit)}%` }} />
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.mobileMetaRow}>
                  <span className={styles.mobileMetaLabel}>Revenue</span>
                  <div className={styles.rev}>
                    ${p?.revenue.toLocaleString()}
                    <small>generated</small>
                  </div>
                </div>

                <div className={styles.mobileMetaRow}>
                  <span className={styles.mobileMetaLabel}>Expires</span>
                  <span className={`${styles.exp} ${p.status === 'expired' ? styles.past : ''}`}>
                    {p.expires ?? '—'}
                  </span>
                </div>

                <div className={styles.mobileActs}>
                  <PromoActions
                    promo={p}
                    expanded={expanded === p.id}
                    onToggleExpand={() => setExpanded(prev => prev === p.id ? null : p.id)}
                    onToggleStatus={() => handleToggle(p.id)}
                    onDelete={() => handleDelete(p.id)}
                  />
                </div>

                {expanded === p.id && <PromoUsagePanel promo={p} />}
              </article>
            ))
          )}
        </div>
      </div>

      {/* ── Create modal ── */}
      {showModal && (
        <div className={styles.overlay} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className={styles.modal}>
            <div className={styles.mHead}>
              <h2>New Promo Code</h2>
              <button onClick={() => setShowModal(false)}>{Ico.close}</button>
            </div>

            <div className={styles.mBody}>
              {error && (
                <div className={styles.error}>
                  {error}
                </div>
              )}
              <div className={styles.field}>
                <label>Code</label>
                <div className={styles.codeRow}>
                  <input
                    placeholder="e.g. SUMMER20"
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    maxLength={20}
                  />
                  <button className={styles.genBtn} onClick={() => setCode(genCode())}>
                    Generate
                  </button>
                </div>
              </div>

              <div className={styles.row2}>
                <div className={styles.field}>
                  <label>Discount</label>
                  <input
                    type="number"
                    placeholder="10"
                    min={1}
                    value={discount}
                    onChange={e => setDiscount(e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label>Type</label>
                  <select value={discType} onChange={e => setDiscType(e.target.value as DiscountType)}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">fixed ($)</option>
                  </select>
                </div>
              </div>

              <div className={styles.row2}>
                <div className={styles.field}>
                  <label>Usage Limit</label>
                  <input
                    type="number"
                    placeholder="50"
                    min={1}
                    value={usageLimit}
                    onChange={e => setUsageLimit(e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={expires}
                    onChange={e => setExpires(e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label>Min Order Amount</label>
                  <input
                    type="number"
                    min={1}
                    value={minOrderAmount}
                    onChange={e => setMinOrderAmount(parseFloat(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className={styles.mFoot}>
              <button className={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button className={styles.submitBtn} onClick={handleCreate}>
                {<HiOutlinePlusSmall size={16} />} Create Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={styles.toast}>
          {Ico.check}
          {toast}
        </div>
      )}
    </div>
  );
};

export default CreatorPromotions;